import { Component } from '@theme/component';

const mobileMediaQuery = matchMedia('(max-width: 749px)');

/**
 * Keeps each mobile story moving until its bottom edge has entered the viewport,
 * then holds it in place so the following story can stack over it.
 *
 * @extends {Component<{ story: HTMLElement[] }>}
 */
class ProductStoriesComponent extends Component {
  #resizeObserver = new ResizeObserver(() => this.#scheduleMeasure());

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #animationFrame = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    mobileMediaQuery.addEventListener('change', this.#scheduleMeasure, { signal });
    window.addEventListener('resize', this.#scheduleMeasure, { signal });

    this.#observeStories();
    this.#scheduleMeasure();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeStories();
    this.#scheduleMeasure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#resizeObserver.disconnect();

    if (this.#animationFrame !== null) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #observeStories() {
    this.#resizeObserver.disconnect();

    for (const story of this.refs.story ?? []) {
      this.#resizeObserver.observe(story);
    }

    const stickyAddToCartBar = this.#getStickyAddToCartBar();
    if (stickyAddToCartBar) {
      this.#resizeObserver.observe(stickyAddToCartBar);
    }
  }

  #scheduleMeasure = () => {
    if (this.#animationFrame !== null) return;

    this.#animationFrame = requestAnimationFrame(() => {
      this.#animationFrame = null;
      this.#updateStickyOffsets();
    });
  };

  #updateStickyOffsets() {
    const stories = this.refs.story ?? [];

    if (!mobileMediaQuery.matches || stories.length === 0) {
      for (const story of stories) {
        story.style.removeProperty('--story-sticky-top-mobile');
      }

      this.removeAttribute('data-mobile-sticky-ready');
      return;
    }

    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const configuredTop = Number.parseFloat(this.dataset.stickyTop ?? '0') || 0;
    const bottomClearance = this.#getStickyAddToCartBar()?.getBoundingClientRect().height ?? 0;

    for (const story of stories) {
      const stickyTop = Math.min(configuredTop, viewportHeight - bottomClearance - story.offsetHeight);
      story.style.setProperty('--story-sticky-top-mobile', `${Math.floor(stickyTop)}px`);
    }

    this.setAttribute('data-mobile-sticky-ready', '');
  }

  /** @returns {HTMLElement | null} */
  #getStickyAddToCartBar() {
    const stickyAddToCartBar = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__bar');
    return stickyAddToCartBar instanceof HTMLElement ? stickyAddToCartBar : null;
  }
}

if (!customElements.get('product-stories-component')) {
  customElements.define('product-stories-component', ProductStoriesComponent);
}
